<div class="row wrapper border-bottom white-bg page-heading">
  <div class="col-lg-10">
    <ol class="breadcrumb">
      <li class="breadcrumb-item"><a [routerLink]="['/home']">Home</a></li>
      <li class="breadcrumb-item"><a [routerLink]="['/fire-foresight/incidents']">Fire Foresight</a></li>
      <li class="breadcrumb-item active">Incident Statistics</li>
    </ol>
  </div>
  <div class="col-lg-2 d-flex align-items-center justify-content-end gap-2">
    <small class="text-muted" *ngIf="lastRefresh">{{ lastRefresh | date:'d MMM yyyy HH:mm' }}</small>
    <button class="btn btn-outline-white btn-sm" (click)="load()" [disabled]="loading" title="Refresh">
      <i class="fa fa-sync" [class.fa-spin]="loading"></i>
    </button>
  </div>
</div>

<div class="wrapper wrapper-content animated fadeInRight pt-3">

  <div class="st-topbar">
    <div>
      <span class="st-topbar__breadcrumb">Report Period</span>
      <div class="st-topbar__period">{{ reportPeriod }}</div>
    </div>
    <div class="d-flex align-items-center gap-3 flex-wrap">
      <div class="btn-group" role="group">
        <button type="button" class="btn btn-sm"
          *ngFor="let period of periodOptions"
          [ngClass]="period === periodDays && !usingCustomRange ? 'btn-primary' : 'btn-outline-default'"
          (click)="setPeriod(period)">{{period}}d</button>
      </div>
      <div class="d-flex align-items-center gap-2">
        <input type="text" class="form-control form-control-sm st-date-input" placeholder="From"
          bsDatepicker [bsConfig]="bsDatePickerConfig" [(ngModel)]="customStartDate">
        <span class="text-muted">&ndash;</span>
        <input type="text" class="form-control form-control-sm st-date-input" placeholder="To"
          bsDatepicker [bsConfig]="bsDatePickerConfig" [(ngModel)]="customEndDate">
        <button type="button" class="btn btn-sm"
          [ngClass]="usingCustomRange ? 'btn-primary' : 'btn-outline-default'"
          (click)="applyCustomRange()">Apply</button>
      </div>
    </div>
  </div>

  <div class="alert alert-info" *ngIf="loading">
    <i class="fa fa-spinner fa-spin me-2"></i> Loading incident statistics&hellip;
  </div>

  <div class="alert alert-warning" *ngIf="!loading && noData">
    No incident statistics found for this period.
  </div>

  <ng-container *ngIf="!loading">

    <div class="st-summary">
      <div class="st-metric-card">
        <div class="st-metric-card__label">Total Incidents</div>
        <div class="st-metric-card__value">{{ totalIncidents | number:'1.0-0' }}</div>
        <div class="st-metric-card__sub">Last {{ periodDays }} days</div>
      </div>
      <div class="st-metric-card st-metric-card--amber">
        <div class="st-metric-card__label">Multiple Detections</div>
        <div class="st-metric-card__value">{{ totalMultipleDetections | number:'1.0-0' }}</div>
        <div class="st-metric-card__sub">{{ multipleDetectionsPercent }} of total</div>
      </div>
      <div class="st-metric-card st-metric-card--amber">
        <div class="st-metric-card__label">Escalated</div>
        <div class="st-metric-card__value">{{ totalEscalated | number:'1.0-0' }}</div>
        <div class="st-metric-card__sub">{{ escalatedPercent }} of total</div>
      </div>
    </div>

    <div class="st-chart-card">
      <div class="st-chart-card__title">Incidents by Hour of Day</div>
      <highcharts-chart
        [Highcharts]="Highcharts"
        [constructorType]="chartConstructor"
        [options]="totalChartOptions"
        style="width:100%;height:300px;display:block;">
      </highcharts-chart>
    </div>

    <div class="st-chart-card">
      <div class="st-chart-card__title">Incidents by Final Status by Hour of Day</div>
      <highcharts-chart
        [Highcharts]="Highcharts"
        [constructorType]="chartConstructor"
        [options]="statusChartOptions"
        style="width:100%;height:300px;display:block;">
      </highcharts-chart>
    </div>

    <div class="st-chart-card">
      <div class="st-chart-card__title">Single vs Multiple Detections by Hour of Day</div>
      <highcharts-chart
        [Highcharts]="Highcharts"
        [constructorType]="chartConstructor"
        [options]="detectionsChartOptions"
        style="width:100%;height:300px;display:block;">
      </highcharts-chart>
    </div>

    <div class="st-chart-card">
      <div class="st-chart-card__title">Escalated vs Not Escalated by Hour of Day</div>
      <highcharts-chart
        [Highcharts]="Highcharts"
        [constructorType]="chartConstructor"
        [options]="escalationChartOptions"
        style="width:100%;height:300px;display:block;">
      </highcharts-chart>
    </div>

    <div class="st-chart-card">
      <div class="st-chart-card__title">Incidents by Detection Type by Hour of Day</div>
      <highcharts-chart
        [Highcharts]="Highcharts"
        [constructorType]="chartConstructor"
        [options]="detectionTypeChartOptions"
        style="width:100%;height:300px;display:block;">
      </highcharts-chart>
    </div>

    <div class="st-chart-card">
      <div class="st-chart-card__title">Detections by False Positive Type by Hour of Day</div>
      <highcharts-chart
        [Highcharts]="Highcharts"
        [constructorType]="chartConstructor"
        [options]="falsePositiveTypeChartOptions"
        style="width:100%;height:300px;display:block;">
      </highcharts-chart>
    </div>

    <div class="st-chart-card">
      <div class="st-chart-card__title">Detections by Responsible Party by Hour of Day</div>
      <highcharts-chart
        [Highcharts]="Highcharts"
        [constructorType]="chartConstructor"
        [options]="responsiblePartyChartOptions"
        style="width:100%;height:300px;display:block;">
      </highcharts-chart>
    </div>

    <div class="st-section-title">Incident Statistics by Hour of Day</div>
    <div class="st-table-wrap">
      <table class="st-table">
        <thead>
          <tr>
            <th>Hour</th>
            <th>Total Incidents</th>
            <th>Confirmed</th>
            <th>Single Detection</th>
            <th>Multiple Detections</th>
            <th>Escalated</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of hourlyRows">
            <td>{{ row.hourOfDay.toString().padStart(2, '0') }}:00</td>
            <td>{{ row.totalIncidents | number:'1.1-1' }}</td>
            <td>{{ row.confirmed | number:'1.1-1' }}</td>
            <td>{{ row.singleDetections | number:'1.1-1' }}</td>
            <td>{{ row.multipleDetections | number:'1.1-1' }}</td>
            <td>{{ row.escalated | number:'1.1-1' }}</td>
          </tr>
          <tr *ngIf="hourlyRows.length === 0">
            <td colspan="6" class="st-cell--muted" style="text-align:center">No statistics available.</td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="st-table__totals">
            <td>Total</td>
            <td>{{ totalIncidents | number:'1.1-1' }}</td>
            <td>{{ totalConfirmed | number:'1.1-1' }}</td>
            <td>{{ totalIncidents - totalMultipleDetections | number:'1.1-1' }}</td>
            <td>{{ totalMultipleDetections | number:'1.1-1' }}</td>
            <td>{{ totalEscalated | number:'1.1-1' }}</td>
          </tr>
        </tfoot>
      </table>
    </div>

  </ng-container>

</div>
